import { useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { Headset } from "lucide-react";

import { useTheme } from "../theme/useTheme.js";
import { useAuth } from "./contexts/AuthContext.jsx";
import { getData } from "./hooks/getData";
import { SupportModal } from "../Pages/Support/SupportModal.jsx";
import { canUseSupport, onOpenSupportTicket, TICKET_TYPE_SUPPORT } from "../utils/supportTickets";

export const SupportButton = () => {
  const { theme, neutrals } = useTheme();
  const { user, interceptorReady } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [initialTicketId, setInitialTicketId] = useState(null);
  const [unreadRefresh, setUnreadRefresh] = useState(0);
  const enabled = canUseSupport(user);

  useEffect(() => {
    if (!enabled) return undefined;
    return onOpenSupportTicket((ticketId) => {
      setInitialTicketId(ticketId);
      setIsOpen(true);
    });
  }, [enabled]);

  const { data: badges } = getData(
    `/communication/tickets/?ticket_type=${TICKET_TYPE_SUPPORT}&include=notifications`,
    enabled && interceptorReady,
    unreadRefresh
  );
  const unreadCount = Array.isArray(badges)
    ? badges.reduce((total, badge) => total + (badge.unread_messages_count || 0), 0)
    : 0;

  if (!enabled) return null;

  const handleClose = () => {
    setIsOpen(false);
    setInitialTicketId(null);
    setUnreadRefresh((prev) => prev + 1);
  };

  return (
    <>
      <Tooltip title={user?.role === "superadmin" ? "" : "Dudas sobre la plataforma"} arrow>
      <Box
        component="button"
        type="button"
        data-welcome-tour="support"
        onClick={() => setIsOpen(true)}
        sx={{
          all: "unset",
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          height: 40,
          px: 1.5,
          borderRadius: "999px",
          cursor: "pointer",
          color: theme.palette.text.secondary,
          transition: "background-color 150ms",
          "&:hover": { backgroundColor: neutrals.gray[100] },
        }}
      >
        <Headset size={20} />
        <Typography variant="body2" sx={{ fontWeight: 500, display: { xs: "none", sm: "block" } }}>
          Soporte
        </Typography>
        {unreadCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              width: 13,
              height: 13,
              borderRadius: "50%",
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              fontSize: "0.65rem",
              fontWeight: 600,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </Box>
        )}
      </Box>
      </Tooltip>

      <SupportModal
        open={isOpen}
        onClose={handleClose}
        initialTicketId={initialTicketId}
        onUnreadChange={() => setUnreadRefresh((prev) => prev + 1)}
      />
    </>
  );
};

export default SupportButton;
